import { useMemo } from 'react';
import type { ShareDialogPayload, ShareListItemData } from '@/types';
import { SHARE_PERMISSION } from '@/types';
import { useShareUsersQuery } from '@/api/queries/useShareUsersQuery';
import { getObjectIdFromPayload } from '@/utils/thoughtspot';
import type { SharePermission as ApiSharePermission } from '@/api/generated/schemas';

function toLocalPermission(apiPermission: ApiSharePermission): ShareListItemData['sharedAsPermission'] {
  // NO_ACCESS should never appear in sharedUsers, but falls through to VIEWER safely.
  return apiPermission === 'MODIFY' ? SHARE_PERMISSION.EDITOR : SHARE_PERMISSION.VIEWER;
}

export function useShareDialogUsers(open: boolean, payload: ShareDialogPayload | null) {
  const objectId = payload ? getObjectIdFromPayload(payload) : null;

  const { data, isLoading, error, refetch } = useShareUsersQuery({
    objectId: open ? objectId : null,
  });

  const sharedItems = useMemo<ShareListItemData[]>(
    () =>
      (data?.sharedUsers ?? []).map((u) => ({
        id: u.id,
        name: u.name,
        email: u.email ?? '',
        sharedAsPermission: toLocalPermission(u.sharedAsPermission),
        userRole: u.userRole ?? null,
        shareUnderlyingData: u.shareUnderlyingData,
        previouslyShared: true,
      })),
    [data],
  );

  const availableItems = useMemo<ShareListItemData[]>(
    () =>
      (data?.availableUsers ?? []).map((u) => ({
        id: u.id,
        name: u.name,
        email: u.email,
        sharedAsPermission: SHARE_PERMISSION.VIEWER,
        userRole: u.userRole ?? null,
        shareUnderlyingData: false,
        previouslyShared: false,
      })),
    [data],
  );

  return { sharedItems, availableItems, isLoading, error, refetch, objectId };
}
